import React from 'react';
import PropTypes from 'prop-types';
import { includes, isString, upperFirst, toUpper, isEmpty, get } from 'lodash';

import { FAQ } from 'components/core/FAQ';
import { Section } from 'components/core/Page';
import { DrillDownLinks } from 'components/content/DrillDownLinks';
import BlogPosts from './BlogPosts';
import StaticRVBlogData from './StaticRVBlogData.json';
import PricingStats from './PricingStats';

import './searchContent.scss';

export const enhance = (WrappedComponent) => {
	const WithSearchContentProps = ({
		searchParams = {},
		location = {},
		...rest
	}) => {
		let formattedVehicleName = '';
		if (isString(searchParams.vehicle) && searchParams.vehicle.length > 0) {
			formattedVehicleName =
				searchParams.vehicle.length > 2
					? upperFirst(searchParams.vehicle) // Car, Boat
					: toUpper(searchParams.vehicle); // RV
		}
		return (
			<WrappedComponent
				{...rest}
				searchParams={searchParams}
				location={location}
				city={get(location, 'city', '')}
				neighborhood={
					get(location, 'subLocality') || get(location, 'city', '')
				}
				state={get(location, 'fullState', '')}
				vehicleType={searchParams.vehicle}
				vehicleName={formattedVehicleName}
				isRVStorage={searchParams.vehicle === 'rv'}
				isVehicleStorage={includes(
					['car', 'rv', 'boat'],
					searchParams.vehicle,
				)}
				isCarStorage={searchParams.vehicle === 'car'}
				isCLP={get(searchParams, 'subPageType', '') === 'cityLandingPage'}
				stateAbbreviation={get(location, 'state', '')}
			/>
		);
	};
	return WithSearchContentProps;
};

export function SearchContent({
	state,
	city,
	vehicleType,
	vehicleName,
	isVehicleStorage,
	content = {},
	isCLP,
	faqShowing,
	faqContent,
	stateAbbreviation,
}) {
	const hasNearbyCities = !isEmpty(content.nearbyCities);
	let nearbyCityLinks = hasNearbyCities
		? content.nearbyCities.map((nearbyCity) => {
				return {
					text: `${nearbyCity?.city} Storage Units`,
					href: nearbyCity?.url,
				};
			})
		: [];

	return (
		<div className="search-content">
			{hasNearbyCities && (
				<DrillDownLinks
					headline="Browse Self-Storage Units in Nearby Cities"
					links={nearbyCityLinks}
				/>
			)}
			{!isEmpty(content.pricingStats) && (
				<Section className="bottom-border">
					<PricingStats
						storageType={content.formattedStorageType}
						locationName={`${city}, ${state}`}
						city={city}
						stateAbbreviation={stateAbbreviation}
						isVehicleStorage={isVehicleStorage}
						pricingStats={content.pricingStats}
						isCLP={isCLP}
						isSearchPageVariation={false}
					/>
				</Section>
			)}

			{faqShowing && (
				<Section className="bottom-border">
					<FAQ content={faqContent} />
				</Section>
			)}

			{!!isVehicleStorage && (
				<Section className="top-border">
					<BlogPosts
						headerText={`Helpful resources for storage in ${city}, ${stateAbbreviation}`}
						posts={StaticRVBlogData[vehicleType]}
					/>
				</Section>
			)}
			{isCLP && !isEmpty(content.movingGuidePosts) && (
				<Section className="top-border">
					<BlogPosts
						headerText={`Helpful resources for storage in ${city}, ${stateAbbreviation}`}
						posts={content.movingGuidePosts}
					/>
				</Section>
			)}
			{isCLP &&
				!isEmpty(content.blogPosts) &&
				isEmpty(content.movingGuidePosts) && (
					<Section className="top-border">
						<BlogPosts
							headerText={`Helpful resources for storage in ${city}, ${stateAbbreviation}`}
							posts={content.blogPosts}
						/>
					</Section>
				)}
		</div>
	);
}

SearchContent.propTypes = {
	state: PropTypes.string,
	city: PropTypes.string,
	vehicleType: PropTypes.string,
	isVehicleStorage: PropTypes.bool,
	vehicleName: PropTypes.string,
	stateAbbreviation: PropTypes.string,
	content: PropTypes.shape({
		drillDownLinks: PropTypes.object,
		nearbyCities: PropTypes.array,
		blogPosts: PropTypes.array,
		movingGuidePosts: PropTypes.array,
		pricingStats: PropTypes.object,
		formattedStorageType: PropTypes.string,
		lowPerformingAlt: PropTypes.shape({
			alternateState: PropTypes.string,
			alternateCity: PropTypes.string,
		}),
		averageMonthlyUnitPrice: PropTypes.object,
	}),
	isCLP: PropTypes.bool,
	faqContent: PropTypes.arrayOf(PropTypes.object),
	faqShowing: PropTypes.bool,
};

export default enhance(SearchContent);
